import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Heart,
  Users,
  HandHeart,
  MessageCircle,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/site/SectionHeader";
import TherapistCard from "@/components/site/TherapistCard";
import { BlobA, BlobB, BlobC, SoftLine } from "@/components/site/BlobBackground";
import {
  SITE,
  IMPACT_STATS,
  IMPACT_STATEMENT,
  THERAPISTS,
} from "@/lib/siteConfig";

const PILLAR_ICONS = [
  <Heart className="w-5 h-5" />,
  <Users className="w-5 h-5" />,
  <HandHeart className="w-5 h-5" />,
  <ShieldCheck className="w-5 h-5" />,
];

export default function ProBonoPage() {
  const reduced = useReducedMotion();
  const waLink = `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(
    "Hi! I'd like to know more about pro-bono therapy support."
  )}`;

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden hero-wash bg-grain">
        <BlobA className="absolute -top-24 -right-20 w-[420px] h-[420px] hidden sm:block" />
        <BlobB className="absolute -bottom-20 -left-20 w-[420px] h-[420px] hidden sm:block" />
        <div className="container-narrow relative pt-14 sm:pt-20 lg:pt-24 pb-12 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={reduced ? false : { opacity: 0, y: 10 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(200,106,74,0.12)] text-terracotta text-xs font-semibold tracking-wide"
                data-testid="probono-eyebrow"
              >
                <Sparkles className="w-3.5 h-3.5" /> Pro-Bono Therapy · Empanelled Therapists
              </motion.div>
              <motion.h1
                initial={reduced ? false : { opacity: 0, y: 16 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                data-testid="probono-title"
                className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-ink"
              >
                Therapy that meets{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-terracotta">every wallet.</span>
                  <SoftLine className="absolute -bottom-2 left-0 w-full h-3 z-0" />
                </span>
              </motion.h1>
              <p
                data-testid="probono-subtitle"
                className="mt-5 text-ink-muted text-base sm:text-lg leading-[1.75] max-w-2xl"
              >
                Every month, our empanelled therapists reserve a small number of pro-bono slots
                for those who need support but can't access it easily. All therapists on this page
                are trained, licensed, or held under supervision by The Human Work Project.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a href="#therapists">
                  <Button
                    data-testid="probono-hero-cta"
                    className="bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl px-6 py-6 text-base transition-colors"
                  >
                    Meet our therapists <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <a href={waLink} target="_blank" rel="noreferrer">
                  <Button
                    variant="outline"
                    data-testid="probono-whatsapp-cta"
                    className="rounded-xl px-6 py-6 text-base border-[rgba(74,63,57,0.18)] hover:bg-[rgba(37,211,102,0.10)] transition-colors"
                  >
                    <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" />
                    Request a referral
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={reduced ? false : { opacity: 0, scale: 0.96 }}
                animate={reduced ? undefined : { opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-[4/5] sm:aspect-square w-full max-w-md mx-auto"
              >
                <BlobC className="absolute inset-0 w-full h-full" />
                <div className="absolute inset-6 rounded-[28px] bg-paper border border-[rgba(74,63,57,0.10)] shadow-[0_14px_40px_rgba(31,26,23,0.10)] p-6 flex flex-col">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold">
                    How it works
                  </div>
                  <h3 className="mt-2 font-display text-xl text-ink leading-snug">
                    Simple, quiet, human.
                  </h3>
                  <ol className="mt-5 space-y-4 text-sm">
                    <Step n="1" t="Browse our empanelled therapists" s="See their approach, focus, and languages." />
                    <Step n="2" t="Book a session" s="Direct to each therapist's Propract profile (opening soon)." />
                    <Step n="3" t="Or request a referral" s="Message us on WhatsApp and we'll match you thoughtfully." />
                  </ol>
                  <div className="mt-auto pt-5 text-xs text-ink-muted">
                    Pro-bono slots are limited each month and offered on need-basis.
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT / CONTRIBUTION */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionHeader
                eyebrow={IMPACT_STATEMENT.eyebrow}
                title={IMPACT_STATEMENT.title}
                testidPrefix="impact"
              />
              <div className="mt-5 space-y-4 max-w-prose">
                {IMPACT_STATEMENT.paragraphs.map((p, i) => (
                  <p key={i} className="text-ink-muted text-[15.5px] leading-[1.8]">
                    {p}
                  </p>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-3">
                {IMPACT_STATS.map((s, i) => (
                  <ImpactStat key={s.l} n={s.n} l={s.l} delay={i * 0.04} />
                ))}
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {IMPACT_STATEMENT.pillars.map((p, i) => (
                  <div
                    key={p.title}
                    className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[rgba(200,106,74,0.12)] text-terracotta flex items-center justify-center">
                      {PILLAR_ICONS[i % PILLAR_ICONS.length]}
                    </div>
                    <h3 className="mt-3 font-display text-base sm:text-lg text-ink leading-snug">
                      {p.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-ink-muted leading-[1.7]">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THERAPISTS ROSTER */}
      <section id="therapists" className="container-narrow py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Empanelled therapists"
          title="Meet the therapists."
          subtitle="A small, growing panel of practising therapists offering pro-bono sessions. Book directly with any therapist below — or message us on WhatsApp for a personal referral."
          testidPrefix="therapists"
        />
        <div
          data-testid="therapists-grid"
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {THERAPISTS.map((t, i) => (
            <TherapistCard key={t.slug} therapist={t} index={i} />
          ))}
        </div>

        <div
          data-testid="therapists-onboarding-note"
          className="mt-10 flex items-start gap-3 text-sm text-ink-muted leading-[1.7] bg-[rgba(126,155,134,0.10)] border border-[rgba(95,127,107,0.22)] rounded-xl p-4"
        >
          <Sparkles className="w-4 h-4 mt-0.5 text-moss shrink-0" />
          <p className="max-w-3xl">
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-moss mr-2">
              Growing panel
            </span>
            More therapists from the Therapist Tribe alumni network are being onboarded to the
            empanelled panel over the coming months. If you're an alumnus or a licensed practitioner
            interested in offering pro-bono slots, please reach out.
          </p>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="container-narrow pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-ink text-paper p-8 sm:p-12 lg:p-14">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(50% 60% at 80% 20%, rgba(200,106,74,0.6) 0%, rgba(200,106,74,0) 60%), radial-gradient(40% 50% at 10% 80%, rgba(126,155,134,0.45) 0%, rgba(126,155,134,0) 60%)",
            }}
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#F2D7B6] font-semibold">
                Need support? Or want to give it?
              </div>
              <h3 className="mt-2 font-display text-3xl sm:text-4xl leading-[1.1] text-paper">
                Reach out. We hold this space with care.
              </h3>
              <p className="mt-4 text-[#E7C2B3] leading-[1.75] max-w-xl">
                Whether you're looking for pro-bono therapy for yourself or someone you care about,
                or you're a licensed practitioner wanting to join our empanelled panel — we'd love
                to hear from you.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
              <a href={waLink} target="_blank" rel="noreferrer" className="flex-1">
                <Button
                  data-testid="probono-cta-whatsapp"
                  className="w-full bg-[#25D366] hover:bg-[#22b459] text-white rounded-xl py-6 text-base transition-colors"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              </a>
              <Link to="/contact" className="flex-1">
                <Button
                  data-testid="probono-cta-contact"
                  variant="outline"
                  className="w-full rounded-xl py-6 text-base border-[rgba(255,251,245,0.28)] text-paper bg-transparent hover:bg-[rgba(255,251,245,0.10)] transition-colors"
                >
                  Contact form
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Step({ n, t, s }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-7 h-7 rounded-full bg-[rgba(200,106,74,0.14)] text-terracotta font-display flex items-center justify-center text-sm">
        {n}
      </span>
      <div>
        <div className="text-ink font-medium leading-tight">{t}</div>
        <div className="text-xs text-ink-muted leading-snug mt-0.5">{s}</div>
      </div>
    </li>
  );
}

function ImpactStat({ n, l, delay = 0 }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 8 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.4, delay, ease: [0.16, 1, 0.3, 1] }}
      className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5"
    >
      <div className="font-display text-2xl sm:text-3xl text-terracotta leading-none">{n}</div>
      <div className="mt-1.5 text-sm text-ink-muted leading-snug">{l}</div>
    </motion.div>
  );
}
